import { useState, useEffect, useRef, createRef } from "react";
import axios from "../../../../lib/axios";
import { ADMIN_ORDER_FIND_MANY } from "../../../../lib/endpoints/order";
import { DEFAULT_ROWS_PER_PAGE } from "../../../../config-global";
import { OrderStatusEnum } from "../../../../enums/orderStatus";
import {
  convertOrderStatusCodeToOrderStatusEnum,
  convertOrderStatusEnumToStatusCode,
} from "../../../../utils/convertOrderStatusValue";
import { OrderModel } from "../../../../models/order";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { MerchantUserModel } from "../../../../models/merchantUser";
import moment from "moment";
import { calculateOrderProductUnitByCarts } from "../../../../utils/calculateOrderProductUnitByCarts";
import { convertDateStringToNormalDateTimeString } from "../../../../utils/convertDateFormat";
import { concatenateStrings } from "../../../../utils/concatenateStrings";
import { convertPromotionTypeToText } from "../../../../utils/convertPromotionTypeToText";
// ---------------------------------------------------

interface ReportStatisticType {
  all: number;
  delivered: number;
  partiallyCompletedSale: number;
  returnSale: number;
  failedSale: number;
}

function calculateTotalWithDeliveryFeeSum(orders: OrderModel[]): number {
  return orders.reduce(
    (sum, order: OrderModel) => sum + order?.totoalWithDeliveryFee,
    0
  );
}

const useSalesReportController = () => {
  // ----------------- variables ------------------

  const authData: MerchantUserModel =
    useSelector((state: RootState) => state?.auth?.data) || null!;
  const [data, setData] = useState<OrderModel[]>([]);
  const [filterData, setFilterData] = useState<OrderModel[]>([]);
  const [allData, setAllData] = useState<OrderModel[]>([]);
  const [showInMoney, setShowInMoney] = useState<string>("amount");
  const [dataReportStatistic, setDataReportStatistic] =
    useState<ReportStatisticType>({
      all: 0,
      delivered: 0,
      partiallyCompletedSale: 0,
      returnSale: 0,
      failedSale: 0,
    });

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingGetData, setLoadingGetData] = useState<boolean>(false);
  const [loadingFetchDataMore, setLoadingFetchDataMore] =
    useState<boolean>(false);
  const searchRef = useRef<HTMLInputElement>(null!);
  const startDateRef = useRef<HTMLInputElement>(null!);
  const endDateRef = useRef<HTMLInputElement>(null!);
  const [openMenu, setOpenMenu] = useState(null);
  const [selectedId, setSelectedId] = useState<string>(null!);
  const [selectedData, setSelectedData] = useState<OrderModel>(null!);
  const [currentOrderStatus, setCurrentOrderStatus] = useState<OrderStatusEnum>(
    OrderStatusEnum.Delivered
  );
  const buttonExportCsv = createRef<any>();
  const [shipmentMethod, setShipmentMethod] = useState<string>("All");
  const [paymentMethod, setPaymentMethod] = useState<string>("All");

  // ----------------- functions --------------------

  function filterDataByDate(
    data: OrderModel[],
    startDate: Date,
    endDate: Date
  ): OrderModel[] {
    return data.filter((item) => {
      const createdAt = new Date(item.createdAt).setHours(0, 0, 0, 0);
      return (
        createdAt >= startDate.setHours(0, 0, 0, 0) &&
        createdAt <= endDate.setHours(0, 0, 0, 0)
      );
    });
  }

  const handleFilterData = () => {
    let newData = filterDataByDate(
      allData,
      new Date(startDateRef.current.value),
      new Date(endDateRef.current.value)
    );
    if (shipmentMethod !== "All") {
      newData = newData.filter((item) => item?.orderMethod === shipmentMethod);
    }
    if (paymentMethod !== "All") {
      newData = newData.filter((item) => item?.paymentMethod === paymentMethod);
    }
    setData(newData);
  };

  const handleGetData = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${ADMIN_ORDER_FIND_MANY}`, {
        filter: {
          merchantId: authData?.merchantId,
        },
      });
      const getData: OrderModel[] = res?.data?.filter(
        (item: OrderModel) =>
          item?.status?.code ===
            convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered) ||
          item?.status?.code ===
            convertOrderStatusEnumToStatusCode(OrderStatusEnum.Failed) ||
          item?.status?.code ===
            convertOrderStatusEnumToStatusCode(OrderStatusEnum.Return) ||
          item?.status?.code ===
            convertOrderStatusEnumToStatusCode(OrderStatusEnum.PARTIALLY)
      );
      const newData: OrderModel[] = filterDataByDate(
        getData,
        new Date(),
        new Date()
      );
      setAllData(getData);
      setData(newData);
      setFilterData(
        newData?.filter(
          (item) =>
            item?.status?.code ===
            convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered)
        )
      );
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleGetDataByOrderStatus = async (
    getOrderStatus: OrderStatusEnum
  ) => {
    try {
      setLoadingGetData(true);
      // const res = await axios.post(
      //   `${ADMIN_ORDER_FIND_MANY}?StatusCodeAdmin=${convertOrderStatusEnumToStatusCode(
      //     getOrderStatus
      //   )}`,
      //   {
      //     filter: {
      //       merchantId: authData?.merchantId,
      //     },
      //   }
      // );
      // setData(res?.data);
      setData(
        allData.filter(
          (item) =>
            item?.status?.code ===
            convertOrderStatusEnumToStatusCode(getOrderStatus)
        )
      );
    } catch (err) {
    } finally {
      setLoadingGetData(false);
    }
  };

  const handleRefetchData = async () => {
    try {
      const res = await axios.post(
        `${ADMIN_ORDER_FIND_MANY}?StatusCodeAdmin=${convertOrderStatusEnumToStatusCode(
          currentOrderStatus
        )}`,
        {
          filter: {
            merchantId: authData?.merchantId,
          },
        }
      );
      setData(res?.data);
    } catch (err) {
    } finally {
    }
  };

  const handleGetDataByOrderId = async () => {
    try {
      setLoadingGetData(true);
      const res = await axios.post(ADMIN_ORDER_FIND_MANY, {
        filter: {
          merchantId: authData?.merchantId,
          orderNo: searchRef.current.value,
        },
      });
      setFilterData(res?.data);

      // setData(
      //   allData?.filter((item) =>
      //     item?.orderNo?.includes(searchRef.current.value)
      //   )
      // );
    } catch (err) {
    } finally {
      setLoadingGetData(false);
    }
  };

  const handleCurrentOrderStatusChange = (newValue: OrderStatusEnum) => {
    if (newValue) {
      setCurrentOrderStatus(newValue);
      // handleGetDataByOrderStatus(newValue);
    }
  };

  const handleOpenMenu = (event: any, getId: string) => {
    setOpenMenu(event.currentTarget);
    setSelectedId(getId);
    // setSelectedData(data?.filter((item: any) => item?.id === getId)[0]);
  };

  const handleFetchDataMore = () => {};

  const handlePageChange = (event: any, newPage: any) => {
    if (newPage === countPagination) {
      if (!loadingFetchDataMore) {
        handleFetchDataMore();
      }
    }
    setCurrentPage(newPage);
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };

  const handleOpenForm = () => {
    setOpenMenu(null);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedData(null!);
  };

  const handleClickViewData = () => {
    setOpenForm(true);
    setSelectedData(data?.filter((item: any) => item?.id === selectedId)[0]);
    setOpenMenu(null);
  };

  // ----------------- useEffect --------------------

  useEffect(() => {
    if (currentOrderStatus === OrderStatusEnum.PARTIALLY) {
      setFilterData(
        data?.filter(
          (item) =>
            item?.updateByAdmin === true &&
            item?.status?.code ===
              convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered)
        )
      );
    } else {
      setFilterData(
        data?.filter(
          (item) =>
            item?.status?.code ===
            convertOrderStatusEnumToStatusCode(currentOrderStatus)
        )
      );
    }
  }, [currentOrderStatus, data]);

  const rows = filterData.slice(
    (currentPage - 1) * DEFAULT_ROWS_PER_PAGE,
    currentPage * DEFAULT_ROWS_PER_PAGE
  );
  // const rows=data;
  const countPagination = Math.ceil(data.length / DEFAULT_ROWS_PER_PAGE);

  //   const isSearchNotFound: boolean = !data.length;

  useEffect(() => {
    handleGetData();
    startDateRef.current.value = moment(new Date()).format("yyyy-MM-DD");
    endDateRef.current.value = moment(new Date()).format("yyyy-MM-DD");
  }, []);

  useEffect(() => {
    if (showInMoney === "money") {
      setDataReportStatistic({
        all: calculateTotalWithDeliveryFeeSum(
          data.filter(
            (item) =>
              item?.status?.code ===
                convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered) ||
              item?.status?.code ===
                convertOrderStatusEnumToStatusCode(OrderStatusEnum.Return) ||
              item?.status?.code ===
                convertOrderStatusEnumToStatusCode(OrderStatusEnum.Failed)
          )
        ),
        delivered: calculateTotalWithDeliveryFeeSum(
          data?.filter(
            (item) =>
              item?.status?.code ===
                convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered) &&
              item?.updateByAdmin === false
          )
        ),
        partiallyCompletedSale: calculateTotalWithDeliveryFeeSum(
          data?.filter(
            (item) =>
              item?.updateByAdmin === true &&
              item?.status?.code ===
                convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered)
          )
        ),
        returnSale: calculateTotalWithDeliveryFeeSum(
          data?.filter(
            (item) =>
              item?.status?.code ===
              convertOrderStatusEnumToStatusCode(OrderStatusEnum.Return)
          )
        ),
        failedSale: calculateTotalWithDeliveryFeeSum(
          data?.filter(
            (item) =>
              item?.status?.code ===
              convertOrderStatusEnumToStatusCode(OrderStatusEnum.Failed)
          )
        ),
      });
    } else {
      setDataReportStatistic({
        all: data?.length,
        delivered: data?.filter(
          (item) =>
            item?.status?.code ===
              convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered) &&
            item?.updateByAdmin === false
        )?.length,
        partiallyCompletedSale: data?.filter(
          (item) =>
            item?.updateByAdmin === true &&
            item?.status?.code ===
              convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered)
        )?.length,
        returnSale: data?.filter(
          (item) =>
            item?.status?.code ===
            convertOrderStatusEnumToStatusCode(OrderStatusEnum.Return)
        )?.length,
        failedSale: data?.filter(
          (item) =>
            item?.status?.code ===
            convertOrderStatusEnumToStatusCode(OrderStatusEnum.Failed)
        )?.length,
      });
    }
  }, [data, showInMoney]);

  // filter variables
  const findDataExportCsv = (): any => {
    let result = [];
    for (let i: number = 0; i < filterData?.length; i++) {
      for (let j: number = 0; j < filterData[i]?.carts?.length; j++) {
        const item = filterData[i];
        const el = filterData[i]?.carts[j];
        result.push({
          order_no: item?.orderNo,
          order_number: item?.orderNumber,
          shipment_method: item?.orderMethod,
          payment_method: item?.paymentMethod,
          total_unit: calculateOrderProductUnitByCarts(item?.carts),
          status: convertOrderStatusCodeToOrderStatusEnum(item?.status?.code),
          order_time: convertDateStringToNormalDateTimeString(item?.createdAt),
          riderAssist: item?.riderAssist,
          riderDetail: item?.riderDetail,
          total_product_price: item?.totalNomalPrice,
          vax: item?.totalVatPrice,
          delivery_fee: item?.deliveryFee,
          discount_from_promotion: item?.totalDiscountPriceForPromotion,
          discount_from_coupon: item?.totalDiscountPriceForCoupon,
          total:
            item?.status?.code === 7 || item?.status?.code === 8
              ? `-${item?.totoalWithDeliveryFee}`
              : item?.totoalWithDeliveryFee,
          merchant_store: item?.merchant?.nameLa,
          product: el?.product?.price?.masterProduct?.productNameLa,
          barcode: concatenateStrings(
            el?.product?.price?.masterProduct?.barcodes
          ),
          item_code: el?.product?.price?.masterProduct?.itemcode,
          product_quantity: el?.quantity,
          product_price: el?.productPrice,
          discount: el?.discount,
          promotion: convertPromotionTypeToText(
            el?.product?.promotion?.promotionType
          ),
          total_product: el?.total,
        });
      }
    }
    return result;
  };

  return {
    data,
    loading,
    rows,
    countPagination,
    openForm,
    searchRef,
    handleGetDataByOrderId,
    currentPage,
    loadingFetchDataMore,
    handleOpenMenu,
    handlePageChange,
    openMenu,
    handleCloseMenu,
    handleOpenForm,
    handleCloseForm,
    handleClickViewData,
    selectedData,
    currentOrderStatus,
    handleCurrentOrderStatusChange,
    loadingGetData,
    handleRefetchData,
    startDateRef,
    endDateRef,
    dataReportStatistic,
    handleFilterData,
    shipmentMethod,
    paymentMethod,
    setShipmentMethod,
    setPaymentMethod,
    buttonExportCsv,
    findDataExportCsv,
    setCurrentOrderStatus,
    showInMoney,
    setShowInMoney,
    // isSearchNotFound,
  };
};
export default useSalesReportController;
